import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
const ANCHOR_TOP_INSET = 16;

// Gestures rather than scroll events, so the hook never mistakes its own
// scrolling for the reader taking over.
const PIN_RELEASE_EVENTS = ['wheel', 'touchmove', 'mousedown', 'keydown'];
const getLastUserMessageId = messages => {
  var _messages$findLast;
  return (_messages$findLast = messages.findLast(message => message.role === 'USER')) === null || _messages$findLast === void 0 ? void 0 : _messages$findLast.id;
};
const findMessageElement = (scrollContainer, messageId) => scrollContainer.querySelector(`[data-message-id="${messageId}"]`);
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const getOffsetFromContentTop = (scrollContainer, descendant) => descendant.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top + scrollContainer.scrollTop;
const isTranscriptOverflowing = (scrollContainer, spacer) => scrollContainer.scrollHeight - spacer.getBoundingClientRect().height > scrollContainer.clientHeight;
export const useAutoScroll = ({
  messages,
  conversationId
}) => {
  // Callback refs (state setters, not RefObjects) so the effects below re-run
  // when the transcript mounts after the loading and empty states.
  const [scrollContainer, setScrollContainer] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [spacer, setSpacer] = useState(null);
  const pinRef = useRef(null);
  // Mirrors pinRef.current?.isHeld for consumers - null means there's no
  // active pin to resume (e.g. empty conversation).
  const [isPinHeld, setIsPinHeld] = useState(null);
  // Which way resumePin would scroll from the reader's current position -
  // above the pin (reading a long reply) resumes upward, below it resumes
  // downward, so the affordance's arrow must track this, not assume one way.
  const [resumeDirection, setResumeDirection] = useState(null);
  const updateResumeDirection = useCallback(() => {
    const pin = pinRef.current;
    if (!scrollContainer || !(pin !== null && pin !== void 0 && pin.messageElement.isConnected)) {
      setResumeDirection(null);
      return;
    }
    const pinnedMessageOffset = getOffsetFromContentTop(scrollContainer, pin.messageElement);
    const targetScrollTop = Math.max(0, pinnedMessageOffset - ANCHOR_TOP_INSET);
    setResumeDirection(targetScrollTop < scrollContainer.scrollTop ? 'up' : 'down');
  }, [scrollContainer]);

  // eslint-disable-next-line react-hooks/immutability
  const syncPin = useCallback(() => {
    const pin = pinRef.current;
    if (!scrollContainer || !spacer || !(pin !== null && pin !== void 0 && pin.messageElement.isConnected)) return;
    const pinnedMessageOffset = getOffsetFromContentTop(scrollContainer, pin.messageElement);

    // Only pin once the transcript is actually taller than the viewport - a
    // short reply that already fits should stay put, not get yanked to the top.
    const spacerHeight = spacer.getBoundingClientRect().height;
    const naturalContentHeight = scrollContainer.scrollHeight - spacerHeight;
    if (naturalContentHeight <= scrollContainer.clientHeight) {
      // eslint-disable-next-line react-hooks/immutability
      if (spacerHeight !== 0) spacer.style.blockSize = '0px';
      return;
    }

    // A reply only becomes scrollable once it is taller than the viewport, so
    // reserve the shortfall below the transcript. The spacer shrinks as the
    // reply arrives, which keeps the pinned scroll position reachable.
    const contentBelowPin = naturalContentHeight - pinnedMessageOffset;
    spacer.style.blockSize = `${Math.max(0, scrollContainer.clientHeight - ANCHOR_TOP_INSET - contentBelowPin)}px`;
    if (!pin.isHeld) return;
    const targetScrollTop = Math.max(0, pinnedMessageOffset - ANCHOR_TOP_INSET);
    if (Math.abs(scrollContainer.scrollTop - targetScrollTop) > 1) {
      scrollContainer.scrollTo({
        top: targetScrollTop,
        behavior: pin.behavior
      });
    }
  }, [scrollContainer, spacer]);

  // eslint-disable-next-line react-hooks/immutability
  const resumePin = useCallback(() => {
    const pin = pinRef.current;
    if (!pin) return;
    pin.isHeld = true;
    pin.behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    setIsPinHeld(true);
    syncPin();
  }, [syncPin]);
  useLayoutEffect(() => {
    if (!scrollContainer) return;
    // Scroll anchoring otherwise keeps the viewport glued to the growing end of
    // the transcript, which is the jump-to-bottom being removed here.
    // eslint-disable-next-line react-hooks/immutability
    scrollContainer.style.overflowAnchor = 'none';
  }, [scrollContainer]);
  useEffect(() => {
    if (!scrollContainer || !spacer) return;
    const releasePin = () => {
      if (!pinRef.current) return;
      // A gesture over a reply that doesn't overflow yet isn't "taking over"
      // from anything - there's no pinned position to release.
      if (!isTranscriptOverflowing(scrollContainer, spacer)) return;
      pinRef.current.isHeld = false;
      setIsPinHeld(false);
      updateResumeDirection();
    };
    PIN_RELEASE_EVENTS.forEach(eventName => scrollContainer.addEventListener(eventName, releasePin, {
      passive: true
    }));
    return () => PIN_RELEASE_EVENTS.forEach(eventName => scrollContainer.removeEventListener(eventName, releasePin));
  }, [scrollContainer, spacer, updateResumeDirection]);

  // Keeps the resume arrow's direction current while the reader keeps
  // scrolling around after releasing the pin.
  useEffect(() => {
    if (!scrollContainer) return;
    const handleScroll = () => {
      if (pinRef.current && !pinRef.current.isHeld) updateResumeDirection();
    };
    scrollContainer.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [scrollContainer, updateResumeDirection]);

  // Media loading and viewport changes can reflow the transcript without a
  // message update.
  useEffect(() => {
    if (!scrollContainer || !transcript) return undefined;
    const observer = new ResizeObserver(() => syncPin());
    observer.observe(transcript);
    observer.observe(scrollContainer);
    return () => observer.disconnect();
  }, [scrollContainer, transcript, syncPin]);

  // eslint-disable-next-line react-hooks/immutability
  useLayoutEffect(() => {
    if (!scrollContainer || !spacer) return;
    const dropPin = () => {
      pinRef.current = null;
      setIsPinHeld(null);
      setResumeDirection(null);
      // eslint-disable-next-line react-hooks/immutability
      spacer.style.blockSize = '0px';
    };
    const lastUserMessageId = getLastUserMessageId(messages);
    if (lastUserMessageId === undefined) {
      dropPin();
      return;
    }
    const currentPin = pinRef.current;
    if ((currentPin === null || currentPin === void 0 ? void 0 : currentPin.conversationId) === conversationId && currentPin.messageId === lastUserMessageId) {
      if (!currentPin.messageElement.isConnected) {
        const messageElement = findMessageElement(scrollContainer, lastUserMessageId);
        if (!messageElement) {
          dropPin();
          return;
        }
        currentPin.messageElement = messageElement;
      }
    } else {
      const messageElement = findMessageElement(scrollContainer, lastUserMessageId);
      if (!messageElement) {
        dropPin();
        return;
      }
      pinRef.current = {
        conversationId,
        messageId: lastUserMessageId,
        messageElement,
        behavior: (currentPin === null || currentPin === void 0 ? void 0 : currentPin.conversationId) !== conversationId || prefersReducedMotion() ? 'auto' : 'smooth',
        isHeld: true
      };
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsPinHeld(true);
    }
    syncPin();
  }, [conversationId, messages, scrollContainer, spacer, syncPin]);
  return {
    scrollContainerRef: setScrollContainer,
    transcriptRef: setTranscript,
    spacerRef: setSpacer,
    isPinHeld,
    resumeDirection,
    resumePin
  };
};